export type ReviewFormData = Readonly<{
    stars?: number;
    recommended?: boolean;
    title?: string;
    text?: string;
    fit?: number;
    quality?: number;
    designAppearance?: number;
    assembly?: number;
    comfort?: number;
    pricePerformanceRatio?: number;
    anonymous?: boolean;
    images?: ReviewImage[];
}>;

export type ReviewImage = {
    file: File;
    position: number;
};

export const parseReviewFormData = async (formDataRaw: Record<string, unknown | undefined>): Promise<ReviewFormData> => {
    const stars = parseStars(formDataRaw.stars as string);
    const recommended = parseRecommended(formDataRaw.recommended as string);
    const title = parseTitle(formDataRaw.title as string);
    const text = parseText(formDataRaw.text as string);
    const fit = parseFit(formDataRaw.fit as string);
    const quality = parseQuality(formDataRaw.quality as string);
    const designAppearance = parseDesignAppearance(formDataRaw.designAppearance as string);
    const assembly = parseAssembly(formDataRaw.assembly as string);
    const comfort = parseComfort(formDataRaw.comfort as string);
    const pricePerformanceRatio = parsePricePerformanceRatio(formDataRaw.pricePerformanceRatio as string);
    const anonymous = parseAnonymous(formDataRaw.anonymous as string);
    const images = parseImages(formDataRaw.files as File[]);

    return { stars, recommended, title, text, fit, quality, designAppearance, assembly, comfort, pricePerformanceRatio, anonymous, images };
};

export const parseStars = (stars?: string): number | undefined => {
    if (stars === undefined) {
        return undefined;
    }

    return parseNumber(stars);
};

export const parseRecommended = (recommended?: string): boolean | undefined => {
    if (recommended === undefined) {
        return undefined;
    }

    return parseBoolean(recommended);
};

export const parseTitle = (title?: string): string | undefined => {
    if (title === undefined) {
        return undefined;
    }

    return title;
};

export const parseText = (text?: string): string | undefined => {
    if (text === undefined) {
        return undefined;
    }

    return text;
};

export const parseFit = (fit?: string): number | undefined => {
    if (fit === undefined) {
        return undefined;
    }

    return parseNumber(fit);
};

export const parseQuality = (quality?: string): number | undefined => {
    if (quality === undefined) {
        return undefined;
    }

    return parseNumber(quality);
};

export const parseDesignAppearance = (designAppearance?: string): number | undefined => {
    if (designAppearance === undefined) {
        return undefined;
    }

    return parseNumber(designAppearance);
};

export const parseAssembly = (assembly?: string): number | undefined => {
    if (assembly === undefined) {
        return undefined;
    }

    return parseNumber(assembly);
};

export const parseComfort = (comfort?: string): number | undefined => {
    if (comfort === undefined) {
        return undefined;
    }

    return parseNumber(comfort);
};

export const parsePricePerformanceRatio = (pricePerformanceRatio?: string): number | undefined => {
    if (pricePerformanceRatio === undefined) {
        return undefined;
    }

    return parseNumber(pricePerformanceRatio);
};

export const parseAnonymous = (anonymous?: string): boolean | undefined => {
    if (anonymous === undefined) {
        return undefined;
    }

    return parseBoolean(anonymous);
};

const parseBoolean = (value: string): boolean => {
    if (!['true', 'false'].includes(value)) {
        throw new Error('Value is not a boolean');
    }

    return value === 'true';
};

const parseNumber = (value: string): number => {
    const numberValue = Number(value);
    if (isNaN(numberValue)) {
        throw new Error('Value is not a number');
    }

    return numberValue;
};

export const parseImages = (files?: File[]): ReviewImage[] | undefined => {
    if (files === undefined || files.length === 0) {
        return undefined;
    }

    return files.map((file, position) => ({ file, position }));
};
